import {
  createContext,
  useContext,
  useLayoutEffect,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react'
import { DEFAULT_THEME, THEMES, type Theme } from './types.ts'

interface ThemeContextValue {
  theme: Theme
  themeId: string
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

interface ThemeProviderProps {
  theme: string
  fallback?: string
  children: ReactNode
}

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

export function ThemeProvider({ theme, fallback = DEFAULT_THEME, children }: ThemeProviderProps) {
  const resolvedThemeId = useMemo(() => {
    return THEMES.some((t) => t.id === theme) ? theme : fallback
  }, [theme, fallback])

  useIsomorphicLayoutEffect(() => {
    document.documentElement.setAttribute('data-theme', resolvedThemeId)
  }, [resolvedThemeId])

  const value = useMemo<ThemeContextValue>(
    () => ({
      themeId: resolvedThemeId,
      theme: THEMES.find((t) => t.id === resolvedThemeId) ?? THEMES[0]!,
    }),
    [resolvedThemeId],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider')
  return ctx
}
